'use client';

import { SurveyCGType } from '@/types/graphql/root';
import { DayPinColor } from '@/utils';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import DestinationMarkerSvg from '../destination-marker-svg';
import usePreventBodyScroll from '@/app/hooks/usePreventBodyScroll';

export default function SelectDayModal({
  isVisible,
  focusedDay,
  scrollListToDestOnTop,
  trip,
}: {
  isVisible: boolean;
  focusedDay: number;
  scrollListToDestOnTop: (destId: string, dayNumber: number) => void;
  trip: SurveyCGType;
}) {
  const router = useRouter();

  usePreventBodyScroll(isVisible);

  const selectDayModalDayList = useMemo(() => {
    if (!trip.fromDateTime || !trip.toDateTime) return null;

    const fromDate = dayjs(trip.fromDateTime);
    const toDate = dayjs(trip.toDateTime);
    const duration = toDate.startOf('day').diff(fromDate.startOf('day'), 'day') + 1;
    if (duration <= 0) return null;

    const results: {
      dayNumber: number;
      dateString: string;
    }[] = [];
    for (let i = 1; i <= duration; i++) {
      results.push({
        dayNumber: i,
        dateString: fromDate
          .add(i - 1, 'day')
          .locale('ko')
          .format('MM.DD ddd'),
      });
    }

    return results;
  }, [trip]);

  const height = useMemo(() => {
    if (!isVisible || !selectDayModalDayList) return 0;

    return 88 + 20 + selectDayModalDayList.length * 48 + (selectDayModalDayList.length - 1) * 16 + 48;
  }, [selectDayModalDayList, isVisible]);

  const startAndEndDate = useMemo(() => {
    if (!trip.fromDateTime || !trip.toDateTime) {
      return null;
    }

    const fromDate = dayjs(trip.fromDateTime).format('YYYY.MM.DD');
    const toDate = dayjs(trip.toDateTime).format('YYYY.MM.DD');

    return {
      start: fromDate,
      end: toDate,
    };
  }, [trip]);

  const onClickDay = useCallback(
    (dayNumber: number) => {
      router.back();
      scrollListToDestOnTop('', dayNumber);
    },
    [scrollListToDestOnTop],
  );

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className='fixed w-full max-w-480pxr top-0 z-[100] h-dvh overflow-hidden flex items-center justify-center'
      onClick={() => {
        router.back();
      }}
    >
      <div className='absolute top-0 left-0 w-full h-full bg-custom-black/60 animate-[fadeIn_300ms_ease-in-out]'></div>
      <div
        className='poi-modal-in-main w-full max-w-480pxr bg-white fixed bottom-0 rounded-t-16pxr'
        style={{
          height,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='h-24pxr'></div>
        <div className='flex flex-row justify-between pt-8pxr px-20pxr pb-8pxr'>
          <div className='flex flex-col items-start gap-6pxr h-48pxr'>
            <span className='text-gray-700 body-14-r'>여행 일자</span>
            <span className='sub-heading-16-m text-custom-black'>
              {startAndEndDate?.start} ~ {startAndEndDate?.end}
            </span>
          </div>
        </div>

        <div className='flex flex-col pt-20pxr pb-32pxr gap-16pxr px-20pxr'>
          {selectDayModalDayList?.map(({ dayNumber, dateString }) => {
            let bgColor = DayPinColor.day1;
            if (dayNumber === 1) bgColor = DayPinColor.day1;
            else if (dayNumber === 2) bgColor = DayPinColor.day2;
            else if (dayNumber === 3) bgColor = DayPinColor.day3;
            else if (dayNumber === 4) bgColor = DayPinColor.day4;
            else if (dayNumber === 5) bgColor = DayPinColor.day5;
            else if (dayNumber === 6) bgColor = DayPinColor.day6;

            const isFocused = focusedDay === dayNumber;
            return (
              <button
                key={'daySelectButton_' + dayNumber}
                className={`flex flex-row items-center justify-center gap-12pxr rounded-6pxr box-border h-48pxr ${
                  isFocused ? 'border-2 border-custom-black' : 'border border-gray-300'
                }`}
                onClick={() => onClickDay(dayNumber)}
              >
                <div className='flex flex-row items-center gap-16pxr'>
                  <DestinationMarkerSvg bgColor={bgColor} height={20} width={15} />
                  <span className={`button-16-m ${isFocused ? 'text-custom-black' : 'text-gray-700'}`}>
                    {dayNumber}일차
                  </span>
                </div>
                <span className={`button-14-m text-gray-600`}>{dateString}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
